/**
 * Pure helpers that aggregate SessionRecords into the shapes the Stats and
 * Home pages render. Date math is local-time; the streak boundary follows
 * the user's clock, not UTC.
 */

import type {
  ActivitySessionRecord,
  SessionRecord,
} from "@music-app/persistence";

export interface SummaryStats {
  sessionCount: number;
  totalReviews: number;
  totalCorrect: number;
  /** 0..1, or 0 when no reviews. */
  accuracy: number;
  /** Weighted average response time across all completed sessions. */
  averageResponseMs: number;
}

export interface DailyBucket {
  /** YYYY-MM-DD in local time. */
  date: string;
  reviews: number;
  correct: number;
  /** null when no reviews on that day. */
  accuracy: number | null;
}

/**
 * Aggregate completed sessions. In-progress sessions (endedAt: null) are
 * excluded so a half-finished session doesn't count toward totals.
 */
export function summarizeSessions(sessions: SessionRecord[]): SummaryStats {
  const completed = sessions.filter((s) => s.endedAt !== null);
  const totalReviews = completed.reduce((sum, s) => sum + s.itemsReviewed, 0);
  const totalCorrect = completed.reduce((sum, s) => sum + s.correctCount, 0);
  // averageResponseMs is per-session; weight by that session's review count
  // before averaging across sessions.
  const responseMsTotal = completed.reduce(
    (sum, s) => sum + s.averageResponseMs * s.itemsReviewed,
    0,
  );
  return {
    sessionCount: completed.length,
    totalReviews,
    totalCorrect,
    accuracy: totalReviews > 0 ? totalCorrect / totalReviews : 0,
    averageResponseMs: totalReviews > 0 ? responseMsTotal / totalReviews : 0,
  };
}

/**
 * Build `days` buckets ending at `today` (inclusive). Days with no sessions
 * have `reviews: 0` and `accuracy: null` so the sparkline can render gaps
 * rather than zero-stripes.
 */
export function dailyBuckets(
  sessions: SessionRecord[],
  today: Date,
  days = 14,
): DailyBucket[] {
  const map = new Map<string, { reviews: number; correct: number }>();
  for (const s of sessions) {
    if (s.endedAt === null) continue;
    const key = toLocalDateKey(new Date(s.startedAt));
    const cur = map.get(key) ?? { reviews: 0, correct: 0 };
    cur.reviews += s.itemsReviewed;
    cur.correct += s.correctCount;
    map.set(key, cur);
  }

  const buckets: DailyBucket[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = toLocalDateKey(d);
    const cur = map.get(key);
    if (cur && cur.reviews > 0) {
      buckets.push({
        date: key,
        reviews: cur.reviews,
        correct: cur.correct,
        accuracy: cur.correct / cur.reviews,
      });
    } else {
      buckets.push({ date: key, reviews: 0, correct: 0, accuracy: null });
    }
  }
  return buckets;
}

/**
 * Count of consecutive days ending at `today` that have at least one
 * completed session. Today is included if the user has practiced today;
 * if today is empty, the streak ends yesterday (or zero).
 */
export function currentStreak(sessions: SessionRecord[], today: Date): number {
  const days = new Set<string>();
  for (const s of sessions) {
    if (s.endedAt === null) continue;
    days.add(toLocalDateKey(new Date(s.startedAt)));
  }
  let streak = 0;
  const cursor = new Date(today);
  while (days.has(toLocalDateKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/**
 * Total minutes of active practice logged today across all activity types.
 * Uses wall-clock startedAt/endedAt on SessionRecord (FSRS practice) and
 * ActivitySessionRecord (listen, drill, challenge). In-progress records
 * (endedAt: null) are excluded.
 */
export function todayMinutes(
  sessions: SessionRecord[],
  activitySessions: ActivitySessionRecord[],
  today: Date,
): number {
  const todayKey = toLocalDateKey(today);

  function sumMs(records: Array<{ startedAt: string; endedAt: string | null }>): number {
    return records.reduce((total, r) => {
      if (r.endedAt === null) return total;
      if (toLocalDateKey(new Date(r.startedAt)) !== todayKey) return total;
      return total + (new Date(r.endedAt).getTime() - new Date(r.startedAt).getTime());
    }, 0);
  }

  return Math.round((sumMs(sessions) + sumMs(activitySessions)) / 60_000);
}

function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
